import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import bookingsApi, { type Booking, type BookingStatus } from '@/api/bookings';

export interface AppointmentData {
  id: string;
  /** Backend booking id when this entry came from /bookings/mine */
  bookingId?: string;
  petId: string;
  petName: string;
  petSpecies: 'dog' | 'cat';
  professionalType: string;
  professionalName: string;
  professionalContact?: string;
  service: string;
  date: Date;
  duration: number; // in minutes
  status:
    | 'scheduled'
    | 'confirmed'
    | 'completed'
    | 'cancelled'
    | 'pending-payment';
  location: string;
  notes?: string;
  paymentStatus: 'paid' | 'pending' | 'refunded';
  createdAt: Date;
  /** Invoice id emitted by the backend when the booking was completed */
  invoiceId?: string | null;
  /** Raw backend booking status (for professional-side rendering) */
  rawStatus?: BookingStatus;
  /** Whether this booking was marked as emergency */
  isEmergency?: boolean;
}

function mapBookingToAppointment(b: Booking): AppointmentData {
  // Build a JS Date from the scheduledDate/scheduledTime fields
  const [hStr = '0', mStr = '0'] = (b.scheduledTime || '00:00').split(':');
  const [yyStr, moStr, ddStr] = (b.scheduledDate || '1970-01-01').split('-');
  const date = new Date(
    parseInt(yyStr ?? '1970', 10),
    (parseInt(moStr ?? '1', 10) || 1) - 1,
    parseInt(ddStr ?? '1', 10) || 1,
    parseInt(hStr, 10) || 0,
    parseInt(mStr, 10) || 0,
  );

  // Map backend BookingStatus → calendar UI status (see OwnerCalendar statusConfig)
  const uiStatus: AppointmentData['status'] = (() => {
    switch (b.status) {
      case 'completed':
        return 'completed';
      case 'cancelled':
      case 'no_show':
        return 'cancelled';
      case 'confirmed':
      case 'assigned':
      case 'in_progress':
        return 'confirmed';
      default:
        return 'scheduled';
    }
  })();

  return {
    id: b.id,
    bookingId: b.id,
    petId: b.petId || '',
    petName: b.petName,
    petSpecies: (b.petSpecies as 'dog' | 'cat') ?? 'dog',
    professionalType: b.serviceCategory || 'veterinary',
    professionalName: b.businessName || 'Professional',
    professionalContact: b.businessPhone || b.businessEmail || undefined,
    service:
      b.serviceName || b.reason || b.reasonKey || b.serviceType || 'Appointment',
    date,
    duration: b.duration ?? 30,
    status: uiStatus,
    location: b.businessName || '',
    notes: b.notes || undefined,
    paymentStatus: 'pending',
    createdAt: b.createdAt ? new Date(b.createdAt) : new Date(),
    invoiceId: (b as any).invoiceId ?? null,
    rawStatus: b.status,
    isEmergency: b.isEmergency,
  };
}

interface AppointmentsContextType {
  appointments: AppointmentData[];
  isLoading: boolean;
  refresh: () => Promise<void>;
  addAppointment: (appointment: AppointmentData) => void;
  updateAppointment: (id: string, updates: Partial<AppointmentData>) => void;
  cancelAppointment: (id: string) => void;
  getAppointmentsByPet: (petId: string) => AppointmentData[];
  getAllAppointments: () => AppointmentData[];
  getUpcomingAppointments: () => AppointmentData[];
}

const AppointmentsContext = createContext<AppointmentsContextType | undefined>(
  undefined
);

export function AppointmentsProvider({ children }: { children: ReactNode }) {
  const [appointments, setAppointments] = useState<AppointmentData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Hydrate from the backend — without this, the Calendar stays empty and
  // "clicking" a booking silently does nothing because there are no rows to
  // click.
  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await bookingsApi.list({ limit: 100 });
      setAppointments(res.bookings.map(mapBookingToAppointment));
    } catch (err) {
      console.error('[AppointmentsContext] failed to load bookings:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const addAppointment = (appointment: AppointmentData) => {
    setAppointments((prev) => [...prev, appointment]);
  };

  const updateAppointment = (id: string, updates: Partial<AppointmentData>) => {
    setAppointments((prev) =>
      prev.map((apt) => (apt.id === id ? { ...apt, ...updates } : apt))
    );
  };

  const cancelAppointment = (id: string) => {
    setAppointments((prev) =>
      prev.map((apt) =>
        apt.id === id ? { ...apt, status: 'cancelled' as const } : apt
      )
    );
  };

  const getAppointmentsByPet = (petId: string) => {
    return appointments.filter((apt) => apt.petId === petId);
  };

  const getAllAppointments = () => {
    return appointments;
  };

  const getUpcomingAppointments = () => {
    const now = new Date();
    return appointments
      .filter(
        (apt) =>
          apt.date >= now &&
          apt.status !== 'cancelled' &&
          apt.status !== 'completed'
      )
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  };

  return (
    <AppointmentsContext.Provider
      value={{
        appointments,
        isLoading,
        refresh,
        addAppointment,
        updateAppointment,
        cancelAppointment,
        getAppointmentsByPet,
        getAllAppointments,
        getUpcomingAppointments,
      }}
    >
      {children}
    </AppointmentsContext.Provider>
  );
}

export function useAppointments() {
  const context = useContext(AppointmentsContext);
  if (!context) {
    throw new Error('useAppointments must be used within AppointmentsProvider');
  }
  return context;
}
